import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ShieldCheck, CheckCircle2, XCircle, AlertCircle, Loader2, RotateCw, Mail, Send, ChevronDown, ArrowRight, Mailbox } from "lucide-react";

// Graph-transport email: EXO status strip + one-tap scoped send-policy flow (auto-detects an
// existing policy) + collapsible RBAC diagnostics. Replaces the old manual grant-send block.
export function EmailNotifyPolicy({ s, setNotifyEmail, t, onGoConnection }) {
  const exoOrg = s.exo?.org;
  const exoReady = !!(s.exo?.enabled && exoOrg);
  const mbx = s.notify?.email?.fromMailbox || "";

  const [status, setStatus] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [mailboxes, setMailboxes] = useState(null);
  const [groups, setGroups] = useState(null);
  const [grp, setGrp] = useState(s.exo?.sendPolicyGroup || "");
  const [loading, setLoading] = useState(exoReady);
  const [busy, setBusy] = useState(null); // "create" | "test" | "rbac" | "loadmbx" | "loadgrp"
  const [rbac, setRbac] = useState(null);
  const [showDiag, setShowDiag] = useState(false);
  const [testSent, setTestSent] = useState(false);

  const loadAll = useCallback(async () => {
    if (!exoReady) { setLoading(false); return; }
    setLoading(true);
    try {
      const st = await api.get("/settings/exo/role-status").then((r) => r.data).catch(() => null);
      setStatus(st);
      const [pol, mb, gr] = await Promise.all([
        api.get("/settings/exo/send-policy").then((r) => r.data).catch(() => null),
        api.get("/settings/exo/shared-mailboxes").then((r) => r.data?.mailboxes || []).catch(() => []),
        api.get("/settings/exo/security-groups").then((r) => r.data?.groups || []).catch(() => []),
      ]);
      setPolicy(pol); setMailboxes(mb); setGroups(gr);
      if (pol?.exists) {
        // A policy already exists — pre-select its scope group.
        const match = gr.find((g) => g.PrimarySmtpAddress === pol.scopeSmtp || g.DisplayName === pol.scopeName);
        if (match) setGrp(match.PrimarySmtpAddress);
      } else if (s.exo?.sendPolicyGroup) {
        // Otherwise prefill the group the admin chose in the Setup Wizard (persisted in settings).
        const saved = s.exo.sendPolicyGroup;
        const match = gr.find((g) => g.PrimarySmtpAddress === saved || g.DisplayName === saved);
        setGrp(match ? match.PrimarySmtpAddress : saved);
      }
    } finally { setLoading(false); }
  }, [exoReady]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadMailboxes = async () => {
    setBusy("loadmbx");
    try { const { data } = await api.get("/settings/exo/shared-mailboxes"); setMailboxes(data.mailboxes || []); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const loadGroups = async () => {
    setBusy("loadgrp");
    try { const { data } = await api.get("/settings/exo/security-groups"); setGroups(data.groups || []); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  const createPolicy = async () => {
    if (!mbx || !grp) { toast.error(t("set.emailPolicyNeedBoth")); return; }
    setBusy("create");
    try {
      const { data } = await api.post("/settings/exo/prepare-notify-policy", { mailbox: mbx, group: grp });
      setNotifyEmail("fromMailbox", mbx);
      setNotifyEmail("transport", "graph");
      toast.success(data?.message || t("set.emailPolicyCreated"));
      await loadAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.msg : apiErr(e), { duration: d?.rbacMissing ? 12000 : 5000 });
    } finally { setBusy(null); }
  };

  const sendTest = async () => {
    setBusy("test"); setTestSent(false);
    try { const { data } = await api.post("/settings/exo/send-test-email", {}); toast.success(data.message); setTestSent(true); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  const checkRbac = async () => {
    setBusy("rbac");
    try { const { data } = await api.get("/settings/exo/appaccess-readiness"); setRbac(data); }
    catch (e) { toast.error(apiErr(e)); setRbac(null); } finally { setBusy(null); }
  };

  // ---- Not connected: friendly pointer to the Connection tab -------------------------------
  if (!exoReady) {
    return (
      <div className="mt-4 flex flex-col items-start gap-3 rounded-sm border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="email-exo-not-connected">
        <div className="flex items-start gap-2">
          <Mailbox size={16} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-bold">{t("set.emailExoNotConnected")}</p>
            <p className="mt-0.5 text-xs">{t("set.emailExoConnectFirst")}</p>
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onGoConnection} data-testid="email-go-connection" className="rounded-sm border-amber-300 bg-white text-amber-800 hover:bg-amber-100">
          {t("set.emailGoConnection")} <ArrowRight size={13} className="ml-1.5" />
        </Button>
      </div>
    );
  }

  const connected = !!(status?.roleReady || status?.connected);
  const policyExists = !!policy?.exists;
  const scopeName = policy?.scopeName || policy?.scopeSmtp || "—";

  return (
    <div className="mt-4 space-y-4" data-testid="email-notify-policy">
      {/* (a) EXO status strip */}
      <div className="flex flex-wrap items-center gap-2 rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs" data-testid="email-exo-status">
        <Mailbox size={14} className="shrink-0 text-zinc-500" />
        <span className="font-bold text-zinc-600">{t("set.emailExoStatusTitle")}:</span>
        {connected
          ? <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-bold text-emerald-700" data-testid="email-exo-status-connected"><CheckCircle2 size={12} /> {t("set.emailExoConnected")}</span>
          : <span className="inline-flex items-center gap-1 rounded-sm border border-amber-200 bg-amber-50 px-2 py-0.5 font-bold text-amber-700" data-testid="email-exo-status-pending"><AlertCircle size={12} /> {exoOrg}</span>}
        {policyExists && (
          <span className="inline-flex items-center gap-1 rounded-sm border border-sky-200 bg-sky-50 px-2 py-0.5 font-medium text-sky-800" data-testid="email-policy-exists-pill">
            <ShieldCheck size={12} /> {t("set.emailPolicyExists").replace("{scope}", scopeName)}
          </span>
        )}
        <Button type="button" variant="ghost" size="sm" onClick={loadAll} disabled={loading} data-testid="email-status-refresh" className="ml-auto h-6 rounded-sm px-2 text-[11px] text-zinc-500">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
        </Button>
      </div>

      {loading && <p className="text-xs text-zinc-500" data-testid="email-policy-loading">{t("set.emailPolicyLoading")}</p>}

      {/* Sender mailbox */}
      <div className="space-y-1.5 max-w-md">
        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.emailFrom")}</Label>
        {mailboxes ? (
          <Select value={mbx} onValueChange={(v) => setNotifyEmail("fromMailbox", v)}>
            <SelectTrigger className="rounded-sm border-zinc-300 font-mono text-xs" data-testid="settings-email-from"><SelectValue placeholder={t("set.emailSelectFrom")} /></SelectTrigger>
            <SelectContent>
              {mailboxes.length === 0 && <div className="px-2 py-1.5 text-xs text-zinc-400">{t("set.emailNoMailboxes")}</div>}
              {mailboxes.map((m) => <SelectItem key={m.PrimarySmtpAddress} value={m.PrimarySmtpAddress}>{m.DisplayName} — {m.PrimarySmtpAddress}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex items-center gap-2">
            <Input value={mbx} onChange={(e) => setNotifyEmail("fromMailbox", e.target.value)} placeholder="alerts@yourdomain.com" data-testid="settings-email-from" className="rounded-sm border-zinc-300 font-mono text-xs" />
            <Button type="button" variant="outline" disabled={busy === "loadmbx"} onClick={loadMailboxes} data-testid="settings-email-loadmbx" className="shrink-0 rounded-sm border-zinc-300">
              {busy === "loadmbx" ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
            </Button>
          </div>
        )}
        <p className="text-[11px] text-zinc-400">{t("set.emailFromHint")}</p>
      </div>

      {/* (b) One-tap scoped send-policy flow */}
      <div className="space-y-3 rounded-sm border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800" data-testid="email-scoped-policy">
        <div className="flex items-center gap-2 font-bold"><Send size={14} /> {t("set.emailScopeTitle")}</div>
        {policyExists ? (
          <p data-testid="email-policy-created-note">{t("set.emailPolicyExists").replace("{scope}", scopeName)}</p>
        ) : (
          <p>{t("set.emailGrantBody")}</p>
        )}

        <div className="space-y-1.5">
          <Label className="text-[11px] font-bold uppercase tracking-wider text-sky-700">{t("set.emailGroupLabel")}</Label>
          <div className="flex items-center gap-2">
            {groups ? (
              <Select value={grp} onValueChange={setGrp}>
                <SelectTrigger className="rounded-sm border-sky-300 bg-white font-mono text-xs" data-testid="settings-email-group"><SelectValue placeholder={t("set.emailGroupSelect")} /></SelectTrigger>
                <SelectContent>
                  {groups.length === 0 && <div className="px-2 py-1.5 text-xs text-zinc-400">{t("set.emailNoGroups")}</div>}
                  {groups.map((g) => <SelectItem key={g.PrimarySmtpAddress} value={g.PrimarySmtpAddress}>{g.DisplayName} — {g.PrimarySmtpAddress}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={grp} onChange={(e) => setGrp(e.target.value)} placeholder="mail-sec-group@yourdomain.com" data-testid="settings-email-group" className="rounded-sm border-sky-300 bg-white font-mono text-xs" />
            )}
            <Button type="button" variant="outline" disabled={busy === "loadgrp"} onClick={loadGroups} data-testid="settings-email-loadgrp" className="shrink-0 rounded-sm border-zinc-300 bg-white">
              {busy === "loadgrp" ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
            </Button>
          </div>
          <p className="text-[11px] text-sky-700/80">{t("set.emailGroupHint")}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" onClick={createPolicy} disabled={busy === "create" || !mbx || !grp} data-testid="settings-email-grant" className="rounded-sm bg-primary hover:bg-[#002480]">
            {busy === "create" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <ShieldCheck size={14} className="mr-2" />} {policyExists ? t("set.emailPolicyUpdate") : t("set.emailCreatePolicy")}
          </Button>
          <Button type="button" variant="outline" onClick={sendTest} disabled={busy === "test" || !mbx} data-testid="settings-email-test" className="rounded-sm border-zinc-300 bg-white">
            {busy === "test" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Mail size={14} className="mr-2" />} {t("set.emailTestBtn")}
          </Button>
          {testSent && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700" data-testid="email-test-sent"><CheckCircle2 size={13} /> {t("set.emailTestSent")}</span>}
        </div>
      </div>

      {/* (c) Collapsible diagnostics */}
      <div className="rounded-sm border border-zinc-200 bg-white" data-testid="email-diagnostics">
        <button type="button" onClick={() => setShowDiag((v) => !v)} data-testid="email-diagnostics-toggle" className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-zinc-500 hover:bg-zinc-50">
          <span className="flex items-center gap-2"><ShieldCheck size={14} /> {t("set.emailDiagnostics")}</span>
          <ChevronDown size={15} className={`transition-transform ${showDiag ? "rotate-180" : ""}`} />
        </button>
        {showDiag && (
          <div className="space-y-3 border-t border-zinc-100 p-3 text-xs" data-testid="email-diagnostics-body">
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" onClick={checkRbac} disabled={busy === "rbac"} data-testid="settings-email-rbaccheck" className="rounded-sm border-zinc-300">
                {busy === "rbac" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <ShieldCheck size={14} className="mr-2" />} {t("set.rbacCheck")}
              </Button>
              {rbac && (rbac.ready
                ? <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700" data-testid="rbac-badge-ok"><CheckCircle2 size={13} /> {t("set.rbacReady")}</span>
                : <span className="inline-flex items-center gap-1 rounded-sm border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-bold text-red-700" data-testid="rbac-badge-fail"><XCircle size={13} /> {t("set.rbacNotReady")}</span>)}
            </div>
            {rbac && (
              <div className="space-y-1.5 rounded-sm border border-zinc-200 bg-zinc-50 p-2.5" data-testid="rbac-checklist">
                {[["manageAsApp", "set.rbacChkManageAsApp"], ["exchangeAdminRole", "set.rbacChkExchangeAdmin"], ["cmdlets", "set.rbacChkCmdlets"]].map(([key, label]) => {
                  const v = rbac.checks?.[key];
                  const state = v === true ? "ok" : v === false ? "fail" : "unknown";
                  return (
                    <div key={key} className="flex items-center gap-2" data-testid={`rbac-check-${key}`}>
                      {state === "ok" && <CheckCircle2 size={14} className="shrink-0 text-emerald-600" data-testid={`rbac-check-${key}-ok`} />}
                      {state === "fail" && <XCircle size={14} className="shrink-0 text-red-600" data-testid={`rbac-check-${key}-fail`} />}
                      {state === "unknown" && <AlertCircle size={14} className="shrink-0 text-amber-500" data-testid={`rbac-check-${key}-unknown`} />}
                      <span className={state === "ok" ? "text-emerald-700" : state === "fail" ? "text-red-700" : "text-amber-700"}>
                        {t(label)}{state === "unknown" && <span className="ml-1 text-amber-600/80">— {t("set.rbacChkUnknown")}</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {rbac && !rbac.ready && (
              <p className="text-[11px] text-red-600" data-testid="rbac-fix-hint">
                {rbac.checks?.manageAsApp === false ? t("set.rbacFixManageAsApp")
                  : rbac.checks?.exchangeAdminRole === false ? t("set.rbacFixExchangeAdmin")
                  : t("set.rbacFixHint")}
              </p>
            )}
            {!rbac && <p className="text-[11px] text-zinc-400">{t("set.emailDiagHint")}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
